const HIDDEN_PLATFORM_IDS = new Set([
  "WEB",
  "MOBILE",
  "POS",
  "OTHER",
  "HUNGRY STATION",
  "HUNGRY_STATION",
]);

const PLATFORM_BRAND_COLORS = {
  JAHEZ: "#E84A4A",
  KEETA: "#FFE41F",
  "THE CHEFZ": "#58264A",
  NINJA: "#1DCAD2",
  CHAT_AGENT: "#F2A15F",
  DIGITAL_CATALOG: "#3B82F6",
};

const PLATFORM_COLOR_PALETTE = [
  "#6366F1",
  "#14B8A6",
  "#A855F7",
  "#F43F5E",
  "#84CC16",
  "#0EA5E9",
  "#E11D48",
  "#F59E0B",
  "#10B981",
  "#8B5CF6",
];

export function canonicalPlatformId(value) {
  return String(value || "")
    .toUpperCase()
    .replace(/[\s_-]+/g, " ")
    .trim();
}

export function humanizePlatformKey(key) {
  const label = canonicalPlatformId(key).toLowerCase();
  if (!label) return "";
  return label.replace(/\b\w/g, (char) => char.toUpperCase());
}

function hashString(value) {
  let hash = 0;
  const text = String(value || "");
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  }
  return hash;
}

export function colorForPlatform(key, explicitColor) {
  if (explicitColor) return explicitColor;
  if (PLATFORM_BRAND_COLORS[key]) return PLATFORM_BRAND_COLORS[key];
  const canon = canonicalPlatformId(key);
  const brandMatch = Object.entries(PLATFORM_BRAND_COLORS).find(
    ([brandKey]) => canonicalPlatformId(brandKey) === canon
  );
  if (brandMatch) return brandMatch[1];
  return PLATFORM_COLOR_PALETTE[hashString(key) % PLATFORM_COLOR_PALETTE.length];
}

function isHiddenPlatform(key) {
  const raw = String(key || "").toUpperCase();
  return HIDDEN_PLATFORM_IDS.has(raw) || HIDDEN_PLATFORM_IDS.has(canonicalPlatformId(key));
}

function platformLabel(platform, isArabic) {
  if (isArabic) {
    return platform?.nameAr || platform?.nameEn || platform?.name || humanizePlatformKey(platform?.key);
  }
  return platform?.nameEn || platform?.name || platform?.nameAr || humanizePlatformKey(platform?.key);
}

export function findMatchingPlatform(platforms, rawId) {
  const canon = canonicalPlatformId(rawId);
  if (!canon || !Array.isArray(platforms) || platforms.length === 0) return null;
  return (
    platforms.find((p) => canonicalPlatformId(p.key || p.id) === canon) || null
  );
}

/**
 * Merge GET /platforms catalog with ordersByDeliveryApps so the widget
 * picks up new platforms without a hardcoded list.
 */
export function buildDashboardOrderPlatforms(catalog = [], orderRows = [], isArabic = false) {
  const byCanon = new Map();

  const upsert = (platform) => {
    const key = platform?.key;
    if (!key || isHiddenPlatform(key)) return;
    const canon = canonicalPlatformId(key);
    if (!canon || byCanon.has(canon)) return;
    byCanon.set(canon, {
      id: key,
      key,
      label: platform.label || platformLabel(platform, isArabic),
      color: colorForPlatform(key, platform.color),
    });
  };

  (Array.isArray(catalog) ? catalog : []).forEach(upsert);

  (Array.isArray(orderRows) ? orderRows : []).forEach((row) => {
    const rawId = row?._id;
    if (!rawId || findMatchingPlatform([...byCanon.values()], rawId)) return;
    upsert({
      key: rawId,
      nameEn: humanizePlatformKey(rawId),
      nameAr: humanizePlatformKey(rawId),
    });
  });

  return [...byCanon.values()];
}
